// @flow
import React, { Fragment } from 'react';
import classnames from 'classnames';
import { mobile as mobileCheck } from 'dibs-client-check';
import { FormattedMessage } from 'react-intl';

// Types
import type { RoomItemArg, RoomRoomArg } from '../helpers/types';

import styles from './styles/HoverRoom.scss';

const IMAGE_SIZE = 1200;

type HoverRoomProps = {
    item: RoomItemArg => Object,
    room: RoomRoomArg,
    hoveredInDataTn?: string,
    hoveredOutDataTn?: string,
    position: number,
};

type HoverRoomState = {
    displayDimensions: Object,
    itemSize: number,
    itemBottom: number,
    inHover: boolean,
    placeholderPosition: Object,
    roomVisible: boolean,
    flipDimensions: boolean,
    floatingWrapperSize: Object,
};

class HoverRoom extends React.Component<HoverRoomProps, HoverRoomState> {
    static defaultProps: {};

    constructor(props: HoverRoomProps) {
        super(props);
        this.state = {
            displayDimensions: {
                width: 0,
                height: 0,
            },
            itemSize: 0,
            itemBottom: 0,
            inHover: false,
            placeholderPosition: {
                top: 0,
                left: 0,
            },
            roomVisible: false,
            flipDimensions: false,
            floatingWrapperSize: {
                width: 0,
                height: 0,
            },
        };

        this.itemImage = null;
        this.roomImage = null;

        this.onResize = this.onResize.bind(this);
        this.onRoomVisible = this.onRoomVisible.bind(this);
        this.handleItemPositioning = this.handleItemPositioning.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
    }

    componentDidMount() {
        window.addEventListener('resize', this.onResize);
    }

    componentDidUpdate(prevProps: HoverRoomProps) {
        const {
            room: { src },
            item: { displayDimensions = {} },
        } = this.props;
        const {
            room: { src: prevSrc },
            item: { displayDimensions: prevDisplayDimensions = {} },
        } = prevProps;
        if (src !== prevSrc) {
            this.setState({ roomVisible: false });
            this.handleItemPositioning(this.props);
        } else if (displayDimensions.width !== prevDisplayDimensions.width) {
            // user display measurements were updated
            // re-run display measurements code
            this.onItemVisible();
        }
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    itemImage: any;
    roomImage: any;

    onResize = () => {
        /* istanbul ignore next */
        this.handleItemPositioning(this.props);
    };

    onRoomVisible = () => {
        /* istanbul ignore next - doesn't handle image load */
        this.setState({ roomVisible: true }, () => {
            this.handleItemPositioning(this.props);
        });
    };

    calcItemBottom = (): number => {
        const halfHeight = this.itemImage.clientHeight / 2;
        return halfHeight + 36;
    };

    onItemVisible = /* istanbul ignore next - doesn't handle image load */ () => {
        // This check is needed as seller might be mistaken what is width and height
        // and provide them in a wrong way. So checking the loaded image orientation
        // and comparing with the provided by seller image orientation.
        const {
            item: { inchesDimensions, displayDimensions },
        } = this.props;
        // If image is square the flip should not be checked
        const isImageSquare = inchesDimensions.height / inchesDimensions.width === 1;
        const clientWidthCheck = this.itemImage.clientWidth / this.itemImage.clientHeight < 1;
        const inchesDimensionsCheck = inchesDimensions.width / inchesDimensions.height < 1;
        const flipDimensions =
            !!inchesDimensions.height &&
            !isImageSquare &&
            clientWidthCheck !== inchesDimensionsCheck;

        const displayWidth = displayDimensions.width;
        const displayHeight = displayDimensions.height;

        const itemBottom = this.calcItemBottom();

        this.setState(
            {
                displayDimensions: {
                    width: flipDimensions ? displayHeight : displayWidth,
                    height: flipDimensions ? displayWidth : displayHeight,
                },
                flipDimensions,
                itemBottom,
            },
            () => {
                this.handleItemPositioning(this.props);
            }
        );
    };

    handleMouseEnter = () => {
        // don't update isHover when the mouseenter event was really fired by a touch start
        if (!mobileCheck.hasTouch()) {
            this.setState({
                inHover: true,
                itemBottom: this.calcItemBottom(),
            });
        }
    };

    handleMouseLeave = () => {
        this.setState({ inHover: false });
    };

    handleItemPositioning = (props: HoverRoomProps) => {
        const {
            room: { centerPosition, comparatorWidth },
            item: { inchesDimensions },
        } = props;
        const { flipDimensions } = this.state;
        const proportion = IMAGE_SIZE / this.roomImage.clientWidth;
        const itemInchesWidth = flipDimensions ? inchesDimensions.height : inchesDimensions.width;
        const itemSize =
            ((comparatorWidth.pixel / comparatorWidth.inches) * itemInchesWidth) / proportion;
        const placeholderPosition = {
            top: centerPosition.top / proportion,
            left: centerPosition.left / proportion,
        };

        this.setState({
            itemSize: Math.round(itemSize * 100) / 100,
            placeholderPosition,
            floatingWrapperSize: {
                width: this.roomImage.clientWidth,
                height: this.roomImage.clientHeight,
            },
        });
    };

    renderItemImage = () => {
        const { item, hoveredInDataTn, hoveredOutDataTn } = this.props;
        const {
            inHover,
            itemSize,
            itemBottom,
            placeholderPosition,
            displayDimensions,
        } = this.state;
        const itemDimensionsStyle = {
            width: itemSize + 18,
            marginTop: itemBottom,
        };

        const itemWrapperPlacing = {
            position: 'absolute',
            width: 0,
            height: 0,
            ...placeholderPosition,
        };

        const itemStyle = {
            width: itemSize,
        };

        const floatingWrapperSize = {
            ...this.state.floatingWrapperSize,
        };

        const itemDimensionsClass = classnames(styles.itemDimensions, {
            [styles.isVisible]: inHover,
        });

        return (
            <div
                className={styles.floatingWrapper}
                style={floatingWrapperSize}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                data-tn={inHover ? hoveredInDataTn : hoveredOutDataTn}
            >
                <div style={itemWrapperPlacing}>
                    <img
                        src={item.src}
                        style={itemStyle}
                        className={styles.itemImage}
                        ref={r => {
                            this.itemImage = r;
                        }}
                        onLoad={this.onItemVisible}
                    />
                    <div className={itemDimensionsClass} style={itemDimensionsStyle}>
                        <div
                            className={styles.dimensionsLine}
                            data-tn={`artwork-dimension-line${inHover ? '-visible' : ''}`}
                        />
                        {displayDimensions && displayDimensions.width}
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { room, position } = this.props;
        const { displayDimensions, roomVisible, flipDimensions } = this.state;
        const srcSetProps = room.srcSetProps || null;
        const dimensionsLabel = (
            <FormattedMessage id="HoverRoom.dimensionsLabel" defaultMessage="Dimensions:" />
        );

        return (
            <div className={styles.container}>
                <div className={styles.wrapper}>
                    {roomVisible && this.renderItemImage()}
                    <img
                        src={room.src}
                        {...srcSetProps}
                        className={styles.room}
                        ref={r => {
                            this.roomImage = r;
                        }}
                        onLoad={this.onRoomVisible}
                        data-tn={`hover-room-image-${position}`}
                    />
                </div>
                <div className={styles.credits}>
                    {roomVisible && (
                        <Fragment>
                            {room.credits}
                            {!flipDimensions && displayDimensions && (
                                <div>
                                    {dimensionsLabel}{' '}
                                    {`H ${displayDimensions.height} x W ${displayDimensions.width}`}
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
        );
    }
}

HoverRoom.defaultProps = {
    hoveredInDataTn: 'hovered-in',
    hoveredOutDataTn: 'hovered-out',
};

export default HoverRoom;
